import { useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/hooks/useI18n';
import { CreateOrphanItineraryPayload } from '@/hooks/useOrphanItineraries';

interface CreateOrphanItineraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateOrphanItineraryPayload) => Promise<void>;
}

export const CreateOrphanItineraryModal = ({
  isOpen,
  onClose,
  onSubmit,
}: CreateOrphanItineraryModalProps) => {
  const { t } = useI18n();
  
  const [title, setTitle] = useState('');
  // Un template si misura in giorni, non in date: quelle arrivano dalla travel request.
  const [durationDays, setDurationDays] = useState<string>('3');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setTitle('');
    setDurationDays('3');
    setDescription('');
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = t('validation.titleRequired');
    }
    const parsedDays = Number(durationDays);
    if (!Number.isInteger(parsedDays) || parsedDays < 1) {
      newErrors.durationDays = t('playground.durationDaysInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: CreateOrphanItineraryPayload = {
        title: title.trim(),
        duration_days: Number(durationDays),
        budget: 0, // Default budget for orphan itineraries
        description: description.trim(),
      };

      await onSubmit(payload);
      handleClose();
    } catch (error) {
      // Error handled by onSubmit
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('playground.createItinerary')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('common.saving') : t('common.create')}
          </Button>
        </>
      }
    >
      <div className="px-6 py-4 space-y-4">
        <div>
          <Label htmlFor="title">{t('playground.itineraryTitle')} *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('playground.itineraryTitlePlaceholder')}
            hasError={!!errors.title}
          />
          {errors.title && (
            <p className="text-sm text-red-500 mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <Label htmlFor="durationDays">{t('playground.durationDays')} *</Label>
          <Input
            id="durationDays"
            type="number"
            min={1}
            max={90}
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            hasError={!!errors.durationDays}
          />
          {errors.durationDays ? (
            <p className="text-sm text-red-500 mt-1">{errors.durationDays}</p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">{t('playground.durationDaysHelp')}</p>
          )}
        </div>

        <div>
          <Label htmlFor="description">{t('playground.description')}</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('playground.descriptionPlaceholder')}
            rows={4}
          />
        </div>
      </div>

    </Modal>
  );
};
